import { collection, getDocs, limit, orderBy, query, where, type Timestamp } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces'

export type JoinCodeRole = 'ADMIN' | 'EDITOR' | 'VIEWER' | 'HELPER'

export type CreateJoinCodeRow = {
  displayName: string
  role: JoinCodeRole
  profileId?: string
  isMinor?: boolean
}

export type CreateJoinCodesInput = {
  workspaceId: string
  rows: CreateJoinCodeRow[]
}

export type CreatedJoinCodeResult = {
  id: string
  displayName: string
  role: JoinCodeRole
  code: string
  pin: string
  expiresAtMs: number
}

export type CreateJoinCodesResponse = {
  batchId: string
  codes: CreatedJoinCodeResult[]
}

export type JoinCodeStatus = 'PENDING' | 'CLAIMED' | 'EXPIRED' | 'REVOKED' | 'LOCKED'

export type JoinCodeDoc = {
  id: string
  workspaceId: string
  codeHash: string
  codeHint: string
  pinHash: string
  status: JoinCodeStatus
  failedAttempts: number
  expiresAt: Timestamp | { seconds: number; nanoseconds: number } | null
  createdBy: string
  createdAt: Timestamp | { seconds: number; nanoseconds: number } | null
  batchId?: string
  prepared: {
    displayName: string
    role: JoinCodeRole | 'LEARNER'
    profileId?: string
    isMinor?: boolean
    learnerId?: string
  }
}

export const createJoinCodes = async (
  input: CreateJoinCodesInput,
): Promise<CreateJoinCodesResponse> =>
  callable<CreateJoinCodesInput, CreateJoinCodesResponse>('createJoinCodes', input)

export type ClaimJoinCodeInput = {
  code: string
  pin: string
  consent?: {
    accepted?: boolean
    acknowledged?: boolean
    noticeVersion: string
  }
}

export type ClaimJoinCodeResponse = {
  workspaceId: string
  workspaceName: string
  role: string
}

export const claimJoinCode = async (
  input: ClaimJoinCodeInput,
): Promise<ClaimJoinCodeResponse> =>
  callable<ClaimJoinCodeInput, ClaimJoinCodeResponse>('claimJoinCode', input)

export type ReissueJoinCodeInput = {
  joinCodeId: string
}

export type ReissueJoinCodeResponse = CreatedJoinCodeResult

export const reissueJoinCode = async (
  input: ReissueJoinCodeInput,
): Promise<ReissueJoinCodeResponse> =>
  callable<ReissueJoinCodeInput, ReissueJoinCodeResponse>('reissueJoinCode', input)

export type RevokeJoinCodeInput = {
  joinCodeId: string
}

export type RevokeJoinCodeResponse = {
  success: boolean
}

export const revokeJoinCode = async (
  input: RevokeJoinCodeInput,
): Promise<RevokeJoinCodeResponse> =>
  callable<RevokeJoinCodeInput, RevokeJoinCodeResponse>('revokeJoinCode', input)

export const listJoinCodes = async (workspaceId: string): Promise<JoinCodeDoc[]> => {
  if (isEduDemoWorkspace(workspaceId)) {
    return []
  }
  const q = query(
    collection(firestoreDb, 'join_codes'),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc'),
    limit(200),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<JoinCodeDoc, 'id'>),
  }))
}
